/**
 * Pre-launch CreOS uniquify for Create Campaign.
 *
 * Videos: ≤10 → all; >10 → random 10, rest original. One CreOS job per video.
 * Carousels: ≤10 batches → all; >10 → random 10. One CreOS job per batch
 * (all slide images). Music files are never uniquified.
 *
 * Shared batch_id for the whole launch (same pattern as CreOS → Uniquify).
 * Waits until CreOS finishes each job. Abandoned-job timing lives on
 * creos-api (STUCK_JOB_MINUTES), not here.
 */

import { apiFetch } from "@/api/client"
import type {
  CreosUniqueBatchDetail,
  CreosUniqueJob,
} from "@/api/hooks/creos"
import type { CarouselBatch } from "./state/formReducer"

export const UNIQUIFY_VIDEO_CAP = 10
export const UNIQUIFY_CAROUSEL_CAP = 10
export const CAMPAIGN_UNIQUIFY_SOURCE = "campaign_form"

const POLL_MS = 2500

export type UniquifyLaunchResult = {
  videos: File[]
  carouselBatches: CarouselBatch[]
  uniquifiedCount: number
  skippedCount: number
}

export function pickIndicesForUniquify(
  count: number,
  cap: number = UNIQUIFY_VIDEO_CAP,
): number[] {
  if (count <= 0) return []
  if (count <= cap) {
    return Array.from({ length: count }, (_, i) => i)
  }
  const idxs = Array.from({ length: count }, (_, i) => i)
  for (let i = idxs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[idxs[i], idxs[j]] = [idxs[j], idxs[i]]
  }
  return idxs.slice(0, cap).sort((a, b) => a - b)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchAuthenticatedBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { credentials: "include" })
  if (!response.ok) {
    throw new Error(`Failed to download uniquified file (${response.status})`)
  }
  return response.blob()
}

async function createUniquifyJob(
  files: File[],
  batchId: string,
): Promise<CreosUniqueJob> {
  const fd = new FormData()
  for (const file of files) {
    fd.append("files", file)
  }
  fd.append("batch_id", batchId)
  fd.append("source", CAMPAIGN_UNIQUIFY_SOURCE)
  return apiFetch<CreosUniqueJob>("/api/creos/uniquify", {
    method: "POST",
    body: fd,
  })
}

async function pollBatchUntilDone(
  batchId: string,
  expectedJobs: number,
  onProgress?: (label: string) => void,
): Promise<CreosUniqueBatchDetail> {
  const started = Date.now()
  let batch = await apiFetch<CreosUniqueBatchDetail>(
    `/api/creos/uniquify/batches/${batchId}`,
  )
  while (true) {
    const elapsedSec = Math.round((Date.now() - started) / 1000)
    const done =
      batch.pending_count === 0 && batch.processing_count === 0
    onProgress?.(
      `Uniquifying creatives… ${batch.completed_count}/${expectedJobs} done` +
        (batch.failed_count ? `, ${batch.failed_count} failed` : "") +
        ` (${elapsedSec}s)`,
    )
    if (done) {
      return batch
    }
    await sleep(POLL_MS)
    batch = await apiFetch<CreosUniqueBatchDetail>(
      `/api/creos/uniquify/batches/${batchId}`,
    )
  }
}

function jobFromCreated(
  created: CreosUniqueJob,
  batch: CreosUniqueBatchDetail,
): CreosUniqueJob | undefined {
  return (batch.jobs || []).find((j) => j.public_id === created.public_id)
}

async function downloadCompletedFiles(
  job: CreosUniqueJob,
  fallbacks: File[],
): Promise<File[] | null> {
  if (job.status !== "completed" || !job.s3_urls?.length) {
    return null
  }
  if (job.s3_urls.length !== fallbacks.length) {
    return null
  }
  try {
    const out: File[] = []
    for (let i = 0; i < job.s3_urls.length; i++) {
      const meta = job.s3_urls[i]
      const original = fallbacks[i]
      const downloadUrl = meta.download_url || meta.url
      const blob = await fetchAuthenticatedBlob(downloadUrl)
      out.push(
        new File([blob], meta.filename || original.name, {
          type: meta.content_type || original.type || "application/octet-stream",
        }),
      )
    }
    return out
  } catch {
    return null
  }
}

/**
 * Returns videos + carousel batches with completed CreOS outputs swapped in.
 * Failed / incomplete samples keep the originals. Carousel music is unchanged.
 */
export async function uniquifyCreativesForLaunch(
  input: {
    videos: File[]
    carouselBatches: CarouselBatch[]
  },
  onProgress?: (label: string) => void,
): Promise<UniquifyLaunchResult> {
  const videos = input.videos
  const carouselBatches = input.carouselBatches
  const eligibleCarousels = carouselBatches
    .map((batch, index) => ({ batch, index }))
    .filter(({ batch }) => batch.images.length > 0)

  const videoIndices = pickIndicesForUniquify(videos.length, UNIQUIFY_VIDEO_CAP)
  const carouselPick = pickIndicesForUniquify(
    eligibleCarousels.length,
    UNIQUIFY_CAROUSEL_CAP,
  )
  const selectedCarousels = carouselPick.map((i) => eligibleCarousels[i])

  if (videoIndices.length === 0 && selectedCarousels.length === 0) {
    return {
      videos,
      carouselBatches,
      uniquifiedCount: 0,
      skippedCount: 0,
    }
  }

  const batchId = crypto.randomUUID()
  const createdVideos: { index: number; file: File; job: CreosUniqueJob }[] = []
  const createdCarousels: {
    formIndex: number
    images: File[]
    job: CreosUniqueJob
  }[] = []

  const totalJobs = videoIndices.length + selectedCarousels.length
  let queued = 0

  onProgress?.(
    `Queuing ${totalJobs} uniquify job(s)` +
      ` (${videoIndices.length} video, ${selectedCarousels.length} carousel)…`,
  )

  for (const index of videoIndices) {
    queued += 1
    const file = videos[index]
    onProgress?.(
      `Queuing uniquify ${queued}/${totalJobs}: video ${file.name}`,
    )
    const job = await createUniquifyJob([file], batchId)
    createdVideos.push({ index, file, job })
  }

  for (const { batch, index } of selectedCarousels) {
    queued += 1
    onProgress?.(
      `Queuing uniquify ${queued}/${totalJobs}: carousel ${batch.index} ` +
        `(${batch.images.length} image${batch.images.length === 1 ? "" : "s"})`,
    )
    const job = await createUniquifyJob(batch.images, batchId)
    createdCarousels.push({
      formIndex: index,
      images: batch.images,
      job,
    })
  }

  const creosBatch = await pollBatchUntilDone(batchId, totalJobs, onProgress)

  const nextVideos = [...videos]
  const nextCarousels = carouselBatches.map((b) => ({
    ...b,
    images: [...b.images],
  }))
  let uniquifiedCount = 0
  let skippedCount = 0

  for (const item of createdVideos) {
    const job = jobFromCreated(item.job, creosBatch)
    const files = job ? await downloadCompletedFiles(job, [item.file]) : null
    if (!files) {
      skippedCount += 1
      continue
    }
    nextVideos[item.index] = files[0]
    uniquifiedCount += 1
  }

  for (const item of createdCarousels) {
    const job = jobFromCreated(item.job, creosBatch)
    const files = job ? await downloadCompletedFiles(job, item.images) : null
    if (!files) {
      skippedCount += 1
      continue
    }
    nextCarousels[item.formIndex] = {
      ...nextCarousels[item.formIndex],
      images: files,
    }
    uniquifiedCount += 1
  }

  if (skippedCount > 0) {
    onProgress?.(
      `Uniquify partial: ${uniquifiedCount} uniquified, ${skippedCount} original — uploading…`,
    )
  } else {
    onProgress?.("Uniquify complete — uploading creatives…")
  }

  return {
    videos: nextVideos,
    carouselBatches: nextCarousels,
    uniquifiedCount,
    skippedCount,
  }
}

/** @deprecated Prefer uniquifyCreativesForLaunch — kept for older call sites/tests. */
export async function uniquifyVideosForLaunch(
  videos: File[],
  onProgress?: (label: string) => void,
): Promise<Omit<UniquifyLaunchResult, "carouselBatches">> {
  const result = await uniquifyCreativesForLaunch(
    { videos, carouselBatches: [] },
    onProgress,
  )
  return {
    videos: result.videos,
    uniquifiedCount: result.uniquifiedCount,
    skippedCount: result.skippedCount,
  }
}
